import { Linking, View } from "react-native";
import { AppointmentModel, IAppointment } from "../core/models/AppointmentModel";
import { Badge, Button, Icon, Text, useTheme } from "react-native-paper";
import { AppointmentStatus, HowToPayType } from "../core/models/enums";
import { formatDateTime, formatShortWeekDay } from "../utils/Formatting";
import B from "./Bold";
import { useMemo } from "react";
import { getAppointmentStatus } from "../utils/Procedures";
import { useAppointments } from "../core/api/appointments";
import { useDialog } from "../core/hooks/dialog";

type Props = {
    appointment: AppointmentModel;
    onCancelPress?: (appointment: IAppointment) => void;
    onPress?: (appointment: IAppointment) => void;
}

const AppointmentCard: React.FC<Props> = ({ appointment, onCancelPress, onPress }) => {

    const theme = useTheme();
    const dialog = useDialog();

    const appointments = useAppointments();

    const statusToColor = (status: AppointmentStatus) => {
        switch (status) {
            case AppointmentStatus.PendingConfirmation:
            case AppointmentStatus.PendingPayment:
                return theme.colors.secondary;

            case AppointmentStatus.CancelledByTherapist:
            case AppointmentStatus.CancelledByUser:
                return theme.colors.tertiary;

            case AppointmentStatus.Confirmed:
            default:
                return theme.colors.primary;
        }
    };

    const openMeetingUrl = async () => {
        const url = await appointments.getAppointmentMeetingUrl(appointment.clinic_id, appointment.virtual_therapist_id, appointment.id);
        Linking.openURL(url);
    }

    const payMeeting = async () => {
        if (appointment.how_to_pay == HowToPayType.Manual) {
            dialog
                .openIconDialog("alert-circle-outline",
                    "Pago manual de terapia",
                    "Esta cita requiere un pago manual. Por favor, contacta a tu terapeuta para más información.",
                    "Aceptar");
            return;
        }

        const url = await appointments.getAppointmentPaymentUrl(appointment.clinic_id, appointment.virtual_therapist_id, appointment.id)
            .catch((e: any) => {
                console.error(e);
                return null;
            });

        if (!url) {
            dialog
                .openIconDialog("close-circle-outline",
                    "Error al obtener el link de pago",
                    "No se pudo obtener el link de pago. Por favor, intenta nuevamente más tarde o contacta con tu terapeuta.",
                    "Aceptar");
            return;
        }

        Linking.openURL(url);
    }

    const canBeCacelled = useMemo(() => {
        // An appointment can be cancelled if it's not in progress or completed and it's more than 24 hours in the future
        return !appointment.isInProgress && !appointment.isCompleted
    }, [appointment.status]);

    return (
        <View style={{
            borderRadius: 30,
            padding: 20,
            backgroundColor: theme.colors.surfaceVariant,
            marginBottom: 20
        }}>

            <Text
                style={{ fontSize: 24, color: theme.colors.onSurfaceVariant }}
                onPress={() => onPress?.(appointment)}>
                <B>{appointment?.therapist?.full_name}</B>
            </Text>
            <View style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
            }}>
                <Text style={{ fontSize: 14, color: theme.colors.onSurfaceVariant }}>
                    {formatShortWeekDay(appointment.time_slot)} - {formatDateTime(appointment.time_slot)}
                </Text>
                <Badge style={{
                    alignSelf: 'flex-start',
                    backgroundColor: statusToColor(appointment.status),
                    paddingHorizontal: 10,
                    marginBottom: 5
                }}>
                    {getAppointmentStatus(appointment)}
                </Badge>
            </View>
            <View style={{
                display: 'flex',
                flexDirection: 'row-reverse',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: 10
            }}>
                {
                    appointment.isInProgress && <Button
                        mode="text"
                        labelStyle={{
                            marginLeft: 0,
                            marginVertical: 0,
                            color: theme.colors.primary
                        }}
                        contentStyle={{
                            flexDirection: 'row-reverse',
                        }}
                        icon='arrow-right'
                        onPress={() => { openMeetingUrl() }}>
                        <Text style={{ color: theme.colors.primary }}>Ir a la cita</Text>
                    </Button>
                }
                {
                    (!appointment.is_paid ? (appointment.how_to_pay == HowToPayType.PsicoproPay ? <Button
                        mode="text"
                        labelStyle={{
                            marginLeft: 0,
                            marginVertical: 0,
                            color: theme.colors.primary
                        }}
                        contentStyle={{
                            flexDirection: 'row-reverse',
                        }}
                        icon='arrow-right'
                        onPress={() => { payMeeting() }}>
                        <Text style={{ color: theme.colors.primary }}>Pagar con PSICOPRO Pay</Text>
                    </Button> : <>
                        <Text style={{
                            color: theme.colors.primary
                        }}
                            onPress={() => { payMeeting() }}>
                            <Icon size={16} source="alert-circle-outline" color={theme.colors.primary} />
                            Requiere pago manual
                        </Text>
                    </>) : null)
                }
                {
                    canBeCacelled && <Button
                        mode="text"
                        labelStyle={{
                            marginLeft: 0,
                            marginVertical: 0,
                            color: theme.colors.error
                        }}
                        contentStyle={{
                            flexDirection: 'row-reverse'
                        }}
                        onPress={() => onCancelPress?.(appointment)}>
                        <Text style={{ color: theme.colors.error }}>Cancelar</Text>
                    </Button>
                }
            </View>
        </View >
    );
};

export default AppointmentCard;
